'use client';

import { type ComponentProps } from 'react';
import {
    Root,
    Trigger,
    Portal,
    Overlay,
    Content,
    Title,
    Description,
    Action,
    Cancel,
} from '@radix-ui/react-alert-dialog';

import { cn } from '@/util/utils';
import { buttonVariants } from '@/components/Button/Button';

function AlertDialog({ open, defaultOpen, onOpenChange, ...props }: ComponentProps<typeof Root>) {
    return (
        <Root
            data-slot="alert-dialog"
            open={open}
            defaultOpen={defaultOpen}
            onOpenChange={onOpenChange}
            {...props}
        />
    );
}

function AlertDialogTrigger({ asChild, ref, ...props }: ComponentProps<typeof Trigger>) {
    return <Trigger data-slot="alert-dialog-trigger" asChild={asChild} ref={ref} {...props} />;
}

function AlertDialogPortal({ container, forceMount, ...props }: ComponentProps<typeof Portal>) {
    return (
        <Portal
            data-slot="alert-dialog-portal"
            container={container}
            forceMount={forceMount}
            {...props}
        />
    );
}

function AlertDialogOverlay({
    className,
    forceMount,
    asChild,
    ref,
    ...props
}: ComponentProps<typeof Overlay>) {
    return (
        <Overlay
            data-slot="alert-dialog-overlay"
            className={cn(
                'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50',
                className,
            )}
            forceMount={forceMount}
            asChild={asChild}
            ref={ref}
            {...props}
        />
    );
}

function AlertDialogContent({
    className,
    forceMount,
    onOpenAutoFocus,
    onCloseAutoFocus,
    onEscapeKeyDown,
    onFocusOutside,
    asChild,
    ref,
    ...props
}: ComponentProps<typeof Content>) {
    return (
        <AlertDialogPortal>
            <AlertDialogOverlay />
            <Content
                data-slot="alert-dialog-content"
                className={cn(
                    'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg p-6 shadow-lg duration-200',
                    'bg-card w-[min(420px,calc(100vw-48px))] max-w-none gap-2 rounded-lg p-6 shadow-lg',
                    className,
                )}
                forceMount={forceMount}
                onOpenAutoFocus={onOpenAutoFocus}
                onCloseAutoFocus={onCloseAutoFocus}
                onEscapeKeyDown={onEscapeKeyDown}
                onFocusOutside={onFocusOutside}
                asChild={asChild}
                ref={ref}
                {...props}
            />
        </AlertDialogPortal>
    );
}

function AlertDialogHeader({ className, ref, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot="alert-dialog-header"
            className={cn('flex flex-col gap-2 text-center sm:text-left', className)}
            ref={ref}
            {...props}
        />
    );
}

function AlertDialogFooter({ className, ref, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot="alert-dialog-footer"
            className={cn('mt-3 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
            ref={ref}
            {...props}
        />
    );
}

function AlertDialogTitle({ className, asChild, ref, ...props }: ComponentProps<typeof Title>) {
    return (
        <Title
            data-slot="alert-dialog-title"
            className={cn('text-section-title text-foreground', className)}
            asChild={asChild}
            ref={ref}
            {...props}
        />
    );
}

function AlertDialogDescription({
    className,
    asChild,
    ref,
    ...props
}: ComponentProps<typeof Description>) {
    return (
        <Description
            data-slot="alert-dialog-description"
            className={cn('text-muted-foreground text-sm', className)}
            asChild={asChild}
            ref={ref}
            {...props}
        />
    );
}

function AlertDialogAction({ className, asChild, ref, ...props }: ComponentProps<typeof Action>) {
    return (
        <Action
            className={cn(buttonVariants(), className)}
            asChild={asChild}
            ref={ref}
            {...props}
        />
    );
}

function AlertDialogCancel({ className, asChild, ref, ...props }: ComponentProps<typeof Cancel>) {
    return (
        <Cancel
            className={cn(buttonVariants({ variant: 'outline' }), className)}
            asChild={asChild}
            ref={ref}
            {...props}
        />
    );
}

export {
    AlertDialog,
    AlertDialogPortal,
    AlertDialogOverlay,
    AlertDialogTrigger,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogFooter,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogAction,
    AlertDialogCancel,
};
